import { request } from '@/utils/request';
import axios from 'axios';

interface MessageWall {
  id: number;
  userId: number;
  username: string;
  avatarUrl: string;
  content: string;
  approved: boolean;
  likes: number;
  completed: boolean;
  createdAt: string;
  updatedAt: string;
}

// 用于获取留言列表的响应格式
interface MessageWallListResponse {
  code: number;
  message: string;
  data: {
    data: MessageWall[];
    total: number;
  };
}

// 用于单个留言操作的响应格式
interface MessageWallItemResponse {
  code: number;
  message: string;
  data: MessageWall | null;
}

// 通用响应类型
type MessageWallResponse = MessageWallListResponse | MessageWallItemResponse;

// API基础URL
const API_BASE_URL = 'https://aivitahelper.xyz:8081/api';

// Mock数据，用于API未实现时展示效果
const mockMessages: MessageWall[] = [
  {
    id: 1,
    userId: 1,
    username: "dong",
    avatarUrl: "/uploads/avatars/1_55331143-67ff-4a71-bfcf-4f50f79365c4.jpg",
    content: "希望未来的自己能够找到理想的工作，加油！",
    approved: true,
    likes: 0,
    completed: false,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
  {
    id: 2,
    userId: 2,
    username: "user2",
    avatarUrl: "",
    content: "面试时要保持自信，展示最好的自己",
    approved: true,
    likes: 0,
    completed: false,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
  {
    id: 3,
    userId: 3,
    username: "user3",
    avatarUrl: "",
    content: "每天进步一点点，未来的路会越走越宽",
    approved: true,
    likes: 0,
    completed: false,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
  {
    id: 4,
    userId: 4,
    username: "user4",
    avatarUrl: "",
    content: "坚持学习，不断提升自己的技能，机会总会来的",
    approved: true,
    likes: 0,
    completed: false,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
  {
    id: 5,
    userId: 5,
    username: "user5",
    avatarUrl: "",
    content: "希望明年这个时候能在理想的公司工作",
    approved: true,
    likes: 0,
    completed: false,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
  {
    id: 6,
    userId: 6,
    username: "user6",
    avatarUrl: "",
    content: "相信自己，你比想象中更优秀！",
    approved: true,
    likes: 0,
    completed: false,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  }
];

// 获取留言墙消息 - 直接使用axios而不是request，以获取完整响应
export const getMessageWallMessages = async (page = 0, size = 20): Promise<MessageWallListResponse> => {
  try {
    // 创建一个带有token的axios实例
    const token = localStorage.getItem('token');
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    
    // 直接使用axios发起请求，获取完整响应
    const response = await axios.get(
      `${API_BASE_URL}/message-wall/page?sort=createdAt&approved=true&page=${page}&size=${size}`,
      { headers }
    );
    
    return response.data;
  } catch (error) {
    console.log('API未实现或出错，使用mock数据');
    // 返回Mock数据
    return {
      code: 200,
      message: "操作成功",
      data: {
        data: mockMessages,
        total: mockMessages.length
      }
    };
  }
};

// 获取用户自己的留言
export const getUserMessages = async (): Promise<MessageWallListResponse> => {
  try {
    // 创建一个带有token的axios实例
    const token = localStorage.getItem('token');
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    
    // 直接使用axios发起请求，获取完整响应
    const response = await axios.get(
      `${API_BASE_URL}/message-wall/my?sort=createdAt`,
      { headers }
    );
    
    return response.data;
  } catch (error) {
    console.log('API未实现或出错，使用mock数据');
    // 返回Mock数据（只有前两条是当前用户的）
    const myMessages = mockMessages.slice(0, 2);
    return {
      code: 200,
      message: "操作成功",
      data: {
        data: myMessages,
        total: myMessages.length
      }
    };
  }
};

// 发送留言
export const sendMessage = async (content: string): Promise<MessageWallItemResponse> => {
  try {
    // 创建一个带有token的axios实例
    const token = localStorage.getItem('token');
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    
    // 直接使用axios发起请求，获取完整响应
    const response = await axios.post(
      `${API_BASE_URL}/message-wall`,
      { content },
      { headers }
    );
    
    return response.data;
  } catch (error) {
    console.log('API未实现或出错，使用mock数据');
    // 返回模拟的成功响应
    const newMessage = {
      id: Math.floor(Math.random() * 1000) + 10,
      userId: 1,
      username: "当前用户",
      avatarUrl: "",
      content,
      approved: true,
      likes: 0,
      completed: false,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    return {
      code: 200,
      message: "操作成功",
      data: newMessage
    };
  }
};

// 更新留言
export const updateMessage = async (id: number, content: string): Promise<MessageWallItemResponse> => {
  try {
    // 创建一个带有token的axios实例
    const token = localStorage.getItem('token');
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    
    // 直接使用axios发起请求，获取完整响应
    const response = await axios.put(
      `${API_BASE_URL}/message-wall`,
      { id, content },
      { headers }
    );
    
    return response.data;
  } catch (error) {
    console.log('API未实现或出错，使用mock数据');
    // 返回模拟的成功响应
    return {
      code: 200,
      message: "操作成功",
      data: {
        id,
        userId: 1,
        username: "当前用户",
        avatarUrl: "",
        content,
        approved: true,
        likes: 0,
        completed: false,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      }
    };
  }
};

// 删除留言
export const deleteMessage = async (id: number): Promise<MessageWallItemResponse> => {
  try {
    // 创建一个带有token的axios实例
    const token = localStorage.getItem('token');
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    
    // 直接使用axios发起请求，获取完整响应
    const response = await axios.delete(
      `${API_BASE_URL}/message-wall/${id}`,
      { headers }
    );
    
    return response.data;
  } catch (error) {
    console.log('API未实现或出错，使用mock数据');
    // 模拟成功删除
    return {
      code: 200,
      message: "删除成功",
      data: null
    };
  }
}; 